<script setup lang="ts">
/**
 * A table, edited at full size, with the printed table beside it.
 *
 * The inspector's column is narrow, and a table of six columns is wider than
 * it: the grid scrolls sideways, the toolbar pushes it down, and the table
 * itself is out of sight on the page behind. Here everything the inspector
 * offers has room: the cell tools, the table's look and its settings in a
 * column of tabs on the left, the grid across the rest, and under the grid the
 * table as it will print, following every change as it is made.
 *
 * Nothing is held back until the dialog closes. Each change is made to the
 * report as it happens, as it is in the inspector, so the dialog can be closed
 * at any moment without losing anything, and undone step by step like
 * everything else.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import ModalDialog from '@/components/ui/ModalDialog.vue'
import type { TableSection } from '@/domain/section'
import TableCellsEditor from '@/sections/table/TableCellsEditor.vue'
import TableFields from '@/sections/table/TableFields.vue'
import TablePrintPreview from '@/sections/table/TablePrintPreview.vue'

const { t } = useI18n()

const props = defineProps<{
  open: boolean
  section: TableSection
}>()

const emit = defineEmits<{
  change: [section: TableSection]
  close: []
}>()

const title = computed(() =>
  props.section.title.trim()
    ? t('inspector.table.editorTitle', { title: props.section.title.trim() })
    : t('inspector.table.editorTitleBare'),
)
</script>

<template>
  <ModalDialog :open="open" :title="title" size="full" @close="emit('close')">
    <!-- Built only while open: a closed dialog holding a grid of every cell
         would be a second copy of the table for nothing. -->
    <TableCellsEditor v-if="open" :section="section" layout="wide" @change="emit('change', $event)">
      <template #look>
        <TableFields :section="section" part="look" @change="emit('change', $event)" />
      </template>

      <template #settings>
        <TableFields :section="section" part="settings" @change="emit('change', $event)" />
      </template>

      <template #below="{ target, pick }">
        <TablePrintPreview :section="section" :target="target" @pick="pick" />
      </template>
    </TableCellsEditor>

    <template #actions>
      <button type="button" class="b-btn b-btn--primary" @click="emit('close')">
        {{ t('common.done') }}
      </button>
    </template>
  </ModalDialog>
</template>
